import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { TID } from "@/constants/testIds";
import { formatBRL } from "@/lib/format";

const MAX_ATTEMPTS = 6;
const POLL_INTERVAL = 2000;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("polling");
  const [data, setData] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let cancelled = false;

    async function poll(n) {
      if (cancelled) return;
      setAttempt(n);
      if (n > MAX_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setData(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        setTimeout(() => poll(n + 1), POLL_INTERVAL);
      } catch (err) {
        setStatus("error");
      }
    }
    poll(1);

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Layout title="Pagamento" subtitle="Confirmando sua assinatura">
      <div data-testid={TID.paymentSuccess} className="max-w-2xl mx-auto">
        <Card className="p-10 rounded-2xl border-stone-200 bg-white text-center">
          {status === "polling" && (
            <>
              <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center">
                <Loader2 className="h-7 w-7 text-emerald-700 animate-spin" />
              </div>
              <h2 className="mt-4 font-display text-2xl font-bold">Processando pagamento...</h2>
              <p className="mt-2 text-sm text-stone-500 font-body">Tentativa {attempt} de {MAX_ATTEMPTS}</p>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-7 w-7 text-emerald-700" />
              </div>
              <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight" data-testid={TID.paymentSuccessStatus}>
                Pagamento confirmado!
              </h2>
              <p className="mt-2 text-sm text-stone-600 font-body">
                Sua assinatura foi ativada com sucesso{data?.amount_total ? ` · ${formatBRL((data.amount_total || 0) / 100)}` : ""}.
              </p>
              <Button
                className="mt-6 bg-emerald-800 hover:bg-emerald-900 text-white h-11 px-6 font-body"
                onClick={() => navigate("/dashboard")}
                data-testid="payment-go-dashboard"
              >
                Ir para o dashboard <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          )}
          {(status === "timeout" || status === "expired" || status === "error") && (
            <>
              <div className="mx-auto h-14 w-14 rounded-full bg-rose-50 flex items-center justify-center">
                <XCircle className="h-7 w-7 text-rose-600" />
              </div>
              <h2 className="mt-4 font-display text-2xl font-bold" data-testid={TID.paymentSuccessStatus}>
                {status === "timeout" ? "Tempo esgotado" : status === "expired" ? "Sessão expirada" : "Erro no pagamento"}
              </h2>
              <p className="mt-2 text-sm text-stone-500 font-body">
                Você pode tentar novamente ou voltar à página de planos.
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <Button variant="outline" onClick={() => navigate("/pricing")} data-testid="payment-retry">
                  Voltar para planos
                </Button>
                <Button className="bg-emerald-800 hover:bg-emerald-900 text-white" onClick={() => navigate("/dashboard")}>
                  Ir para dashboard
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </Layout>
  );
}
